import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { BibleVersion } from '@/lib/utils/bible-utils';

const BIBLE_VERSIONS: BibleVersion[] = ['csb', 'esv', 'kjv', 'nasb', 'niv', 'nkjv'];

interface ConversionStatus {
    version: BibleVersion;
    status: 'idle' | 'converting' | 'success' | 'error';
    paragraphCount?: number;
    sectionsUpdated?: number;
    sectionsSkipped?: number;
    passageIdsAssigned?: number;
    passageIdsUnassigned?: number;
    unassignedRefs?: string[];
    error?: string;
}

export default function AdminParagraphConversion() {
    const [conversionStatuses, setConversionStatuses] = useState<Map<BibleVersion, ConversionStatus>>(
        new Map(BIBLE_VERSIONS.map(v => [v, { version: v, status: 'idle' }]))
    );
    const [isConvertingAll, setIsConvertingAll] = useState(false);

    const updateStatus = (version: BibleVersion, updates: Partial<ConversionStatus>) => {
        setConversionStatuses(prev => {
            const newMap = new Map(prev);
            const current = newMap.get(version) || { version, status: 'idle' };
            newMap.set(version, { ...current, ...updates });
            return newMap;
        });
    };

    const convertVersion = async (version: BibleVersion) => {
        if (!confirm(`Are you sure you want to convert ${version}?`)) {
            return;
        }

        updateStatus(version, { status: 'converting', error: undefined });

        const functions = getFunctions();
        const convertFunction = httpsCallable(functions, 'convertTranslationToParagraphsFunction', {
            timeout: 300000 // 5 minutes
        });

        try {
            console.log(`Converting ${version} to paragraphs...`);
            const result = await convertFunction({ version }) as any;
            console.log(`Successfully converted ${version}:`, result);

            updateStatus(version, {
                status: 'success',
                paragraphCount: result.data?.paragraphCount,
                sectionsUpdated: result.data?.sectionsUpdated,
                sectionsSkipped: result.data?.sectionsSkipped,
                passageIdsAssigned: result.data?.passageIdsAssigned,
                passageIdsUnassigned: result.data?.passageIdsUnassigned,
                unassignedRefs: result.data?.unassignedRefs
            });
        } catch (error: any) {
            console.error(`Failed to convert ${version}:`, error);
            updateStatus(version, {
                status: 'error',
                error: error.message || 'Unknown error'
            });
        }
    };

    const handleConvertAll = async () => {
        if (!confirm('Are you sure?')) {
            return false;
        }
        
        setIsConvertingAll(true);

        for (const version of BIBLE_VERSIONS) {
            await convertVersion(version);
        }

        setIsConvertingAll(false);
    };

    const handleConvertSingle = async (version: BibleVersion) => {
        await convertVersion(version);
    };

    const getStatusIcon = (status: ConversionStatus['status']) => {
        switch (status) {
            case 'converting':
                return <Loader2 className="h-5 w-5 animate-spin text-blue-500" />;
            case 'success':
                return <CheckCircle className="h-5 w-5 text-green-500" />;
            case 'error':
                return <XCircle className="h-5 w-5 text-red-500" />;
            default:
                return <FileText className="h-5 w-5 text-muted-foreground" />;
        }
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Bible Paragraph Generation
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">
                            Convert verse-based translations to paragraph-based format for the reading view.
                        </p>
                        <Button
                            onClick={handleConvertAll}
                            disabled={isConvertingAll}
                            size="sm"
                        >
                            {isConvertingAll ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Converting All...
                                </>
                            ) : (
                                'Convert All Versions'
                            )}
                        </Button>
                    </div>

                    <div className="space-y-2">
                        {BIBLE_VERSIONS.map(version => {
                            const status = conversionStatuses.get(version);
                            if (!status) return null;

                            return (
                                <div
                                    key={version}
                                    className="flex items-center justify-between p-3 border rounded-lg"
                                >
                                    <div className="flex items-center gap-3">
                                        {getStatusIcon(status.status)}
                                        <div>
                                            <div className="font-medium uppercase">{version}</div>
                                            {status.status === 'success' && status.paragraphCount && (
                                                <div className="text-xs text-muted-foreground">
                                                    {status.paragraphCount.toLocaleString()} paragraphs
                                                    {status.sectionsUpdated !== undefined && (
                                                        <> • {status.sectionsUpdated} sections updated</>
                                                    )}
                                                    {status.sectionsSkipped !== undefined && status.sectionsSkipped > 0 && (
                                                        <> ({status.sectionsSkipped} skipped)</>
                                                    )}
                                                    {status.passageIdsAssigned !== undefined && (
                                                        <> • {status.passageIdsAssigned} passage IDs assigned</>
                                                    )}
                                                    {status.passageIdsUnassigned !== undefined && status.passageIdsUnassigned > 0 && (
                                                        <span className="text-orange-500"> ({status.passageIdsUnassigned} unassigned!)</span>
                                                    )}
                                                    {status.unassignedRefs && status.unassignedRefs.length > 0 && (
                                                        <div className="text-orange-500 mt-1">
                                                            Unassigned: {status.unassignedRefs.join(', ')}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {status.status === 'error' && status.error && (
                                                <div className="text-xs text-red-500">
                                                    {status.error}
                                                </div>
                                            )}
                                            {status.status === 'converting' && (
                                                <div className="text-xs text-blue-500">
                                                    Converting...
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <Button
                                        onClick={() => handleConvertSingle(version)}
                                        disabled={status.status === 'converting' || isConvertingAll}
                                        size="sm"
                                        variant="outline"
                                    >
                                        {status.status === 'converting' ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Converting
                                            </>
                                        ) : status.status === 'success' ? (
                                            'Reconvert'
                                        ) : (
                                            'Convert'
                                        )}
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

